import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';

import TabContentItem from 'rebook/apps/base/components/TabContent/TabContentItem';
import debounce from 'rebook/apps/utils/debounce';

import styles from './style.scss';


class TabContentContainer extends React.Component {
  static getLabels(childrenArray, hasPropChildren) {
    return childrenArray.map(
      (item, index) => ({
        label: hasPropChildren ? item.label : item.props.label,
        url: hasPropChildren ? item.url : item.props.url,
        index,
        component: hasPropChildren ? item.component : null,
        isHidden: false,
      }));
  }

  constructor(props) {
    super(props);
    const hasPropChildren = props.components.length > 0;
    const childrenArray = hasPropChildren
      ? props.components
      : React.Children.toArray(props.children);
    const tabList = TabContentContainer.getLabels(childrenArray, hasPropChildren);

    const labelListContainerWidth = 0;
    const itemListWidth = tabList.map(() => ({ width: 0, currentWidth: 0 }));
    this.listContainer = React.createRef();

    this.state = {
      selectedTabIndex: props.defaultItem,
      childrenArray,
      tabList,
      labelListContainerWidth,
      itemListWidth,
      isHidden: false,
      isActiveHiddenTab: false,
      toggleDropDown: false,
      hiddenTabWidth: 96,
      listPaddingSum: 48,
      maxTabWidth: 170,
    };
  }

  getInitialLabelsState(width) {
    const {
      tabList, labelListContainerWidth, selectedTabIndex,
      itemListWidth, isHidden, toggleDropDown,
      hiddenTabWidth, listPaddingSum, maxTabWidth,
    } = this.state;
    const containerWidth = this.listContainer.current
      ? this.listContainer.current.offsetWidth
      : width || labelListContainerWidth;

    let itemsWidthSum = isHidden ? hiddenTabWidth + listPaddingSum : listPaddingSum;
    itemListWidth.map((item) => {
      itemsWidthSum += item.currentWidth;
    });

    if (!containerWidth || itemsWidthSum === 0) {
      return null;
    }
    if (toggleDropDown) {
      this.setState({ toggleDropDown: false });
      return null;
    }

    const widthDiff = containerWidth - itemsWidthSum;
    let newItemLabels = tabList;
    let newItemListWidth = itemListWidth;
    let newItemsWidthSum = 0;
    if (widthDiff <= 0) {
      for (let i = tabList.length - 1; i > 0; i--) {
        if (!tabList[i].isHidden) {
          newItemLabels[i].isHidden = true;
          newItemListWidth[i] = {
            width: newItemListWidth[i].width,
            currentWidth: 0,
          };
        }
        newItemsWidthSum = isHidden ? hiddenTabWidth + listPaddingSum : listPaddingSum;
        newItemListWidth.map((item) => {
          newItemsWidthSum += item.currentWidth;
        });
        if (containerWidth - newItemsWidthSum >= maxTabWidth) {
          break;
        }
      }
      this.setState({
        tabList: newItemLabels,
        itemListWidth: newItemListWidth,
        isHidden: true,
        isActiveHiddenTab: this.isHiddenActiveTab(newItemLabels, selectedTabIndex),
        labelListContainerWidth: containerWidth,
      });
    }
    else if (widthDiff > maxTabWidth) {
      for (let i = 0; i < tabList.length; i++) {
        if (tabList[i].isHidden) {
          newItemLabels[i].isHidden = false;
          newItemListWidth[i] = {
            width: newItemListWidth[i].width,
            currentWidth: newItemListWidth[i].width,
          };
          newItemsWidthSum = isHidden ? hiddenTabWidth + listPaddingSum : listPaddingSum;
          newItemListWidth.map((item) => {
            newItemsWidthSum += item.currentWidth;
          });
          if (containerWidth - newItemsWidthSum >= 0) {
            break;
          }
        }
      }
      this.setState({
        tabList: newItemLabels,
        itemListWidth: newItemListWidth,
        isHidden: this.isHiddenTabs(newItemLabels),
        isActiveHiddenTab: this.isHiddenActiveTab(newItemLabels, selectedTabIndex),
        labelListContainerWidth: containerWidth,
      });
    }
  }

  isHiddenActiveTab(tabList, selectedTabIndex) {
    return tabList.filter((item) => item.isHidden && item.index === selectedTabIndex).length > 0;
  }

  isHiddenTabs(tabList) {
    return tabList.filter((item) => item.isHidden).length > 0;
  }

  handleWindowResize() {
    debounce(this.getInitialLabelsState(), 200);
  }

  componentDidMount() {
    this.getInitialLabelsState();
    window.addEventListener('resize', () => this.handleWindowResize());
  }

  componentWillUnmount() {
    window.removeEventListener('resize', () => this.handleWindowResize());
  }

  handleSelectTab(tabIndex) {
    const { tabList, selectedTabIndex } = this.state;
    if (tabIndex !== selectedTabIndex) {
      this.setState({
        selectedTabIndex: tabIndex,
        isActiveHiddenTab: this.isHiddenActiveTab(tabList, tabIndex),
        toggleDropDown: false,
      });
      if (isFunction(this.props.handleChange)) {
        const tabInfo = tabList.filter((item) => item.index === tabIndex)[0];
        const { index, url } = tabInfo;
        this.props.handleChange({ index, url });
      }
    }
  }

  getListContainer(elem) {
    const { labelListContainerWidth, toggleDropDown } = this.state;
    const listContainerWidth = get(this.listContainer, 'current.offsetWidth', 0);
    if (elem && !toggleDropDown && labelListContainerWidth !== listContainerWidth) {
      debounce(this.getInitialLabelsState(elem.offsetWidth), 200);
    }
  }

  getItemWidth(elem, index) {
    const { itemListWidth, toggleDropDown } = this.state;
    const offsetWidth = get(elem, 'offsetWidth', 0);
    if (elem && !toggleDropDown && itemListWidth[index].currentWidth !== offsetWidth) {
      let newItemListWidth = itemListWidth;
      newItemListWidth[index] = {
        width: elem.offsetWidth,
        currentWidth: elem.offsetWidth,
      };
      this.setState(() => {
        return { itemListWidth: newItemListWidth };
      });
    }
  }

  toggleHiddenTabs() {
    const { toggleDropDown } = this.state;
    this.setState({ toggleDropDown: !toggleDropDown });
  }

  handleDropDownBlur() {
    this.setState({ toggleDropDown: false });
  }

  componentDidUpdate(prevProps) {
    const { props } = this;
    if (!isEqual(props.children, prevProps.children)) {
      const hasPropChildren = props.components.length > 0;
      const childrenArray = hasPropChildren
        ? props.components
        : React.Children.toArray(props.children);
      this.setState({ childrenArray });
    }
  }

  render() {
    const {
      selectedTabIndex, tabList, childrenArray,
      isHidden, toggleDropDown, isActiveHiddenTab,
    } = this.state;
    const { tabsPosition, components, tabsListExtraClassName } = this.props;

    return (
      <div className={styles.tabContentContainer}>
        <div ref={this.listContainer} className={styles.tabLabelsContainer}>
          <ul
            ref={(elem) => { this.getListContainer(elem); }}
            className={classNames(
              styles.tabsNavigation,
              styles[`tabsPosition${tabsPosition}`],
              tabsListExtraClassName
            )}>
            {tabList.map((item) => {
              const isActive = selectedTabIndex === item.index;
              const key = `${item.label}-${item.index}`;
              if (!item.isHidden) {
                return (
                  <li
                    ref={(elem) => { debounce(this.getItemWidth(elem, item.index), 200); }}
                    className={classNames(isActive
                      ? styles.tabsNavigationContainerActive
                      : styles.tabsNavigationContainer
                    )}
                    key={key}>
                    <div className={isActive ? styles.tabLeftBorderActive : styles.tabLeftBorder} />
                    <div className={styles.tabItem} onClick={() => { this.handleSelectTab(item.index); }}>
                      <span className={styles.tabLabelText}>
                        {item.label}
                      </span>
                    </div>
                    <div className={isActive ? styles.tabRightBorderActive : styles.tabRightBorder} />
                  </li>
                );
              }
            })}
          </ul>
          {isHidden && (
            <div className={classNames(styles.hiddenTabsContainer)}>
              <div className={isActiveHiddenTab ? styles.tabLeftBorderActive : styles.tabLeftBorder} />
              <div
                className={classNames(styles.hiddenTabs, isActiveHiddenTab && styles.hiddenTabsActive)}
                onClick={() => { this.toggleHiddenTabs(); }}>
                <span className={styles.tabLabelTextHidden}>
                  ...
                </span>
              </div>
              <div className={isActiveHiddenTab ? styles.tabRightBorderActive : styles.tabRightBorder} />
            </div>
          )}
        </div>
        {toggleDropDown && (
          <ul className={styles.toggleMenu} tabIndex="-1" onBlur={() => { this.handleDropDownBlur(); }}>
            {tabList.map((item) => {
              const isActive = selectedTabIndex === item.index;
              const key = `${item.label}-${item.index}`;
              if (item.isHidden) {
                return (
                  <li
                    ref={(elem) => { debounce(this.getItemWidth(elem, item.index), 200); }}
                    onClick={() => { this.handleSelectTab(item.index); }}
                    className={classNames(
                      styles.tabsNavigationHiddenContainer,
                      isActive && styles.tabsNavigationHiddenContainerActive
                    )}
                    key={key}>
                    <div className={styles.tabItem}>
                      <span className={styles.tabLabelText}>
                        {item.label}
                      </span>
                    </div>
                  </li>
                );
              }
            })}
          </ul>
        )}
        {components.length > 0
          ? (
            <TabContentItem>
              {get(childrenArray.filter((item, index) => selectedTabIndex === index)[0], 'component', null)}
              {/* {childrenArray.filter((item, index) => selectedTabIndex === index)[0]} */}
            </TabContentItem>
          )
          : (
            childrenArray.filter((item, index) => selectedTabIndex === index)
          )
        }
      </div>
    );
  }
};

TabContentContainer.defaultProps = {
  tabsPosition: 'left',
  defaultItem: 0,
  components: [],
  tabsListExtraClassName: '',
};

/*
 * For use whithout jsx:
 *
 * let components = [
 *  {
 *    label: 'first',
 *    url: '/first',
 *    component: <div>First</div>
 *  }, {...}, ...
 * ];
 * <TabContentContainer components={components} />
 */

TabContentContainer.propTypes = {
  defaultItem: PropTypes.number,
  tabsListExtraClassName: PropTypes.string,
  tabsPosition: PropTypes.oneOf(['left', 'center', 'right']),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  components: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
      component:
        PropTypes.oneOfType([
          PropTypes.arrayOf(PropTypes.node),
          PropTypes.node,
        ]),
    }),
  ),
};

export default TabContentContainer;
